"use client";

import { CaretLeft, CaretRight } from "@repo/ui/components/icons";
import { cn } from "@repo/ui/lib/utils";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

// A horizontal shelf of cards, like an app store row: scroll-snap does the
// swiping; on phones the next card peeks in, on wider screens arrow buttons
// page through it. RTL: scrollLeft starts at 0 and goes negative, so "next"
// (toward later cards) scrolls left.
export function Shelf({ header, children }: { header: React.ReactNode; children: React.ReactNode[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const offset = Math.abs(track.scrollLeft);
    setAtStart(offset <= 1);
    setAtEnd(offset + track.clientWidth >= track.scrollWidth - 1);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, children.length]);

  const page = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: -direction * track.clientWidth * 0.9, behavior: "smooth" });
  };

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        {header}
        <div className="hidden shrink-0 gap-1.5 sm:flex">
          <ArrowButton label="قبلی" disabled={atStart} onClick={() => page(-1)}>
            <CaretRight size={16} />
          </ArrowButton>
          <ArrowButton label="بعدی" disabled={atEnd} onClick={() => page(1)}>
            <CaretLeft size={16} />
          </ArrowButton>
        </div>
      </div>
      <div
        ref={trackRef}
        onScroll={measure}
        className="-mx-3 flex snap-x snap-mandatory scroll-px-3 gap-3 overflow-x-auto px-3 pt-1 pb-3 [scrollbar-width:none] sm:mx-0 sm:scroll-px-0 sm:px-0 lg:gap-4 [&::-webkit-scrollbar]:hidden"
      >
        {children.map((child, index) => (
          <div
            key={index}
            className="shrink-0 basis-[78%] snap-start sm:basis-[calc((100%-1.5rem)/3)] lg:basis-[calc((100%-3rem)/4)]"
          >
            {child}
          </div>
        ))}
      </div>
    </section>
  );
}

function ArrowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "bg-background flex h-8 w-8 items-center justify-center rounded-full shadow-sm transition-opacity",
        disabled ? "opacity-40" : "hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
